// The bar at the top of the window (AC-UI-1, AC-UX-1): open, enhance, zoom,
// export; the file name, the page in view, and what the app is doing. (Undo,
// redo, add a picture and paste are in the dock on the left, D-69.)
import type { Session, Status } from '../model.ts';
import type { Theme } from '../theme.ts';
import { EditIcon, EnhanceIcon, ExportIcon, MoonIcon, OpenIcon, Spinner, SunIcon, ZoomInIcon, ZoomOutIcon } from './icons.tsx';
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEPS } from '../model.ts';
import { Logo } from './Logo.tsx';

export function TopBar(props: {
  status: Status;
  session?: Session;
  /** the page in view (0-based) */
  page: number;
  canExport: boolean;
  fileInput: React.RefObject<HTMLInputElement | null>;
  onOpen: (file: File) => void;
  onExport: () => void;
  onHome: () => void;
  theme: Theme;
  onToggleTheme: () => void;
  /** the zoom in effect (factor: share of 100 %) */
  zoom: { mode: 'fixed' | 'width' | 'page'; factor: number };
  onZoomStep: (dir: 1 | -1) => void;
  onZoomSet: (z: { mode: 'fixed' | 'width' | 'page'; factor: number }) => void;
  /** debug mode is on (D-45): a badge that opens its panel */
  debug: boolean;
  onDebug: () => void;
  canEnhance: boolean;
  /** the page in view is a scan: Enhance is pointed out */
  scanInView: boolean;
  onEnhance: () => void;
  /** set while Enhance runs: stops it */
  onCancelRun?: () => void;
  /** View or Edit mode (D-74); unset when the document can only be viewed (no toggle) */
  mode?: 'view' | 'edit';
  /** the mode can be switched now (no question about an edit open) */
  canMode: boolean;
  onMode: () => void;
}) {
  const { status, session } = props;
  const busy = status.kind !== 'ready';
  return (
    <header className="topbar-row">
      <button data-testid="home" className="brand brand-btn" title="Home" aria-label="FlexPDF home" onClick={props.onHome}>
        <Logo size={26} />
        <span>FlexPDF</span>
      </button>
      <label className={`btn${busy ? ' is-disabled' : ''}`} title="Open a PDF (Ctrl+O)">
        <OpenIcon />
        <span>Open</span>
        <input
          ref={props.fileInput}
          data-testid="file-input"
          className="visually-hidden"
          type="file"
          accept="application/pdf,.pdf"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Cleared, so choosing the same file again opens it again.
            e.target.value = '';
            if (file) props.onOpen(file);
          }}
        />
      </label>
      <span className="divider" />
      {session && (
        <button
          data-testid="enhance"
          className={`btn${props.scanInView ? ' btn-accent' : ''}`}
          title="Enhance: recognise the text of scanned pages, so it can be selected"
          disabled={busy || !props.canEnhance}
          onClick={props.onEnhance}
        >
          <EnhanceIcon />
          <span>Enhance</span>
        </button>
      )}
      {session && props.mode && (
        // Off: View mode (select, copy, Enhance, Find, fill fields); on: every change of the page.
        <button
          data-testid="mode-edit"
          className="btn"
          aria-pressed={props.mode === 'edit'}
          title={props.mode === 'edit' ? 'Back to View mode: the page can no longer be changed' : 'Edit PDF: change text and pictures on the page'}
          disabled={busy || !props.canMode}
          onPointerDown={(e) => e.preventDefault() /* keeps the line editor's focus: leaving Edit mode ends the edit without applying it */}
          onClick={props.onMode}
        >
          <EditIcon />
          <span>Edit PDF</span>
        </button>
      )}
      <span className="divider" />
      <div className="doc-info">
        {busy && status.kind !== 'error' && <Spinner />}
        <span data-testid="status" data-status={status.kind} className="doc-name" title={session?.name}>
          {status.kind === 'loading' && 'Starting…'}
          {status.kind === 'busy' && `${status.what}…`}
          {status.kind === 'ready' && (session ? session.name : 'No file open')}
          {status.kind === 'error' && status.message}
        </span>
        {props.onCancelRun && (
          <button data-testid="enhance-cancel" className="btn" onClick={props.onCancelRun}>
            Cancel
          </button>
        )}
        {session && (
          <span data-testid="page-indicator" className="page-indicator">
            Page {props.page + 1} / {session.sizes.length}
          </span>
        )}
      </div>
      <span className="spacer" />
      {props.debug && (
        <button data-testid="debug-badge" className="debug-badge" title="Debug mode is on: show the log" onClick={props.onDebug}>
          Debug
        </button>
      )}
      {session && (
        <div className="zoom" role="group" aria-label="Zoom">
          <button data-testid="zoom-out" className="btn icon-btn" aria-label="Zoom out" title="Zoom out (Ctrl −)" disabled={props.zoom.factor <= ZOOM_MIN + 1e-3} onClick={() => props.onZoomStep(-1)}>
            <ZoomOutIcon />
          </button>
          <select
            data-testid="zoom-select"
            className="zoom-select"
            aria-label="Zoom level"
            title="Zoom (Ctrl 0: 100 %)"
            value={props.zoom.mode === 'fixed' ? String(props.zoom.factor) : props.zoom.mode}
            onChange={(e) => {
              const v = e.target.value;
              props.onZoomSet(v === 'width' || v === 'page' ? { mode: v, factor: props.zoom.factor } : { mode: 'fixed', factor: Number(v) });
            }}
          >
            <option value="width">{props.zoom.mode === 'width' ? `Fit width (${Math.round(props.zoom.factor * 100)} %)` : 'Fit width'}</option>
            <option value="page">{props.zoom.mode === 'page' ? `Fit page (${Math.round(props.zoom.factor * 100)} %)` : 'Fit page'}</option>
            {ZOOM_STEPS.map((z) => (
              <option key={z} value={String(z)}>
                {Math.round(z * 100)} %
              </option>
            ))}
          </select>
          <button data-testid="zoom-in" className="btn icon-btn" aria-label="Zoom in" title="Zoom in (Ctrl +)" disabled={props.zoom.factor >= ZOOM_MAX - 1e-3} onClick={() => props.onZoomStep(1)}>
            <ZoomInIcon />
          </button>
        </div>
      )}
      <button data-testid="theme-toggle" className="btn icon-btn" aria-label={props.theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={props.theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={props.onToggleTheme}>
        {props.theme === 'dark' ? <SunIcon /> : <MoonIcon />}
      </button>
      <button data-testid="export" className="btn btn-primary" title="Export an edited copy (the original file is not changed)" disabled={busy || !props.canExport} onClick={props.onExport}>
        <ExportIcon />
        <span>Export copy</span>
      </button>
    </header>
  );
}
